// src/main.jsx
import React, { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { SnackbarProvider, useSnackbar } from "notistack";

import RootLayout from "./Root";
import Welcome from "./pages/Welcome";
import Landing from "./pages/Landing";
import Dashboard from "./user/Dashboard";
import Login from "./pages/Login";

import AdminLayout from "./components/admin/Layout";
import AdminDashboard from "./admin/Dashboard";
import CodesPage from "./admin/Codes";
import Users from "./admin/Users";
import CardsPage from "./admin/Cards";
import TransactionsPage from "./admin/Transactions";
import KycPage from "./admin/Kyc";
import CurrenciesPage from "./admin/Currencies";
import SettingsPage from "./admin/Settings";
import LogsPage from "./admin/Logs";

import Register from "./pages/Register";
import Cards from "./pages/Cards";
import Cardview from "./pages/CardView";
import PoissonModel from "./pages/PoissonModel";
import CardApplication from "./pages/CardApplication";
import InternationalTransfer from "./pages/InternationalTransfer";
import AccountSettings from "./pages/AccountSettings";
import DepositFunds from "./pages/DepositFunds";
import SendMoney from "./pages/SendMoney";
import KycForm from "./pages/KycForm";
import TermsAndCondition from "./pages/TermsAndCondition";
import TransactionHistory from "./pages/TransactionHistory";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import KYCAML from "./pages/KYCAML";
import Security from "./pages/Security";
import CookiePolicy from "./pages/CookiePolicy";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Support from "./pages/Support";

import ProtectedRoute from "./components/ProtectedRoute";

/* ---------------------------
   OPTIONAL: ERUDA INIT
   If you have a useEruda() hook in Root or elsewhere, you can call it here.
   import useEruda from "./hooks/useEruda";
   useEruda();
---------------------------- */

/* ---------------------------
   ROUTES
---------------------------- */
const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      { path: "/", element: <Welcome /> },
      { path: "/landing", element: <Landing /> },
      { path: "/login", element: <Login /> },

      // Legal Pages
      { path: "/privacy", element: <PrivacyPolicy /> },
      { path: "/terms", element: <Terms /> },
      { path: "/kyc-aml", element: <KYCAML /> },
      { path: "/security", element: <Security /> },
      { path: "/cookies", element: <CookiePolicy /> },
      { path: "/about", element: <About /> },
      { path: "/contact", element: <Contact /> },
      { path: "/support", element: <Support /> },

      { path: "/register", element: <Register /> },
      { path: "/kyc-verification", element: <KycForm /> },
      { path: "/terms-condition-policy", element: <TermsAndCondition /> },

      {
        path: "/user/dashboard",
        element: (
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        ),
      },

      {
        path: "/cards",
        element: (
          <ProtectedRoute>
            <Cards />
          </ProtectedRoute>
        ),
      },

      {
        path: "/testpredict",
        element: (
          <ProtectedRoute>
            <PoissonModel />
          </ProtectedRoute>
        ),
      },

      {
        path: "/card-view",
        element: (
          <ProtectedRoute>
            <Cardview />
          </ProtectedRoute>
        ),
      },

      {
        path: "/card-application",
        element: (
          <ProtectedRoute>
            <CardApplication />
          </ProtectedRoute>
        ),
      },

      {
        path: "/account-settings",
        element: (
          <ProtectedRoute>
            <AccountSettings />
          </ProtectedRoute>
        ),
      },

      {
        path: "/deposits",
        element: (
          <ProtectedRoute>
            <DepositFunds />
          </ProtectedRoute>
        ),
      },

      {
        path: "/local-transfer",
        element: (
          <ProtectedRoute>
            <SendMoney />
          </ProtectedRoute>
        ),
      },

      {
        path: "/international-transfer",
        element: (
          <ProtectedRoute>
            <InternationalTransfer />
          </ProtectedRoute>
        ),
      },

      {
        path: "/transactions",
        element: (
          <ProtectedRoute>
            <TransactionHistory />
          </ProtectedRoute>
        ),
      },

      // Admin
      {
        path: "/admin",
        element: (
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminLayout />
          </ProtectedRoute>
        ),
        children: [
          { path: "dashboard", element: <AdminDashboard /> },
          { path: "users", element: <Users /> },
          { path: "cards", element: <CardsPage /> },
          { path: "transactions", element: <TransactionsPage /> },
          { path: "kyc", element: <KycPage /> },
          { path: "currencies", element: <CurrenciesPage /> },
          { path: "codes", element: <CodesPage /> },
          { path: "logs", element: <LogsPage /> },
          { path: "settings", element: <SettingsPage /> },
        ],
      },
    ],
  },
]);

/* ---------------------------
   SNACKBAR LISTENER
---------------------------- */
const SnackbarListener = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const handleSnackbar = (e) => {
      // guard: event.detail might be missing or malformed
      const { message = "", severity = "default" } = e.detail || {};
      enqueueSnackbar(message, { variant: severity });
    };

    window.addEventListener("showSnackbar", handleSnackbar);
    return () => window.removeEventListener("showSnackbar", handleSnackbar);
  }, [enqueueSnackbar]);

  return children;
};

/* ---------------------------
   RENDER
---------------------------- */
const rootEl = document.getElementById("root");

if (!rootEl) {
  throw new Error("Root element with id 'root' not found. Make sure your index.html has <div id=\"root\"></div>");
}

createRoot(rootEl).render(
  <React.StrictMode>
    <SnackbarProvider maxSnack={3} autoHideDuration={4000}>
      <SnackbarListener>
        <RouterProvider router={router} />
      </SnackbarListener>
    </SnackbarProvider>
  </React.StrictMode>
);